import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Trophy, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { visibleInterval } from "../lib/visibleInterval";

const GOLD = "#FFD700";
const CONFETTI_COLORS = ["#FFD700", "#FF007F", "#00F0FF", "#39FF14", "#9D00FF", "#FFE600"];

function Confetti() {
  const bits = useMemo(() => Array.from({ length: 46 }, (_, i) => ({
    left: Math.random() * 100, delay: Math.random() * 0.6, dur: 1.8 + Math.random() * 1.6,
    size: 6 + Math.random() * 8, rot: Math.random() * 360, color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    drift: (Math.random() - 0.5) * 160,
  })), []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {bits.map((b, i) => (
        <span key={i} className="ach-pop-confetti absolute top-0" style={{
          left: `${b.left}%`, width: b.size, height: b.size * 0.45, background: b.color, boxShadow: `0 0 6px ${b.color}`,
          animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s`, "--rot": `${b.rot}deg`, "--drift": `${b.drift}px`,
        }} />
      ))}
    </div>
  );
}

function PopupCard({ a, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 7000);
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  return (
    <div data-testid="achievement-unlock-popup" onClick={onClose}
         className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-[#05020F]/85 backdrop-blur-md ach-pop-fade cursor-pointer">
      <div className="ach-pop-rays pointer-events-none absolute w-[160vmax] h-[160vmax]" aria-hidden="true" />
      <Confetti />
      <div className="ach-pop-card relative flex flex-col items-center text-center max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3 font-heading text-xs sm:text-sm uppercase tracking-[0.45em]" style={{ color: GOLD, textShadow: `0 0 12px ${GOLD}` }}>
          <Sparkles size={16} /> Achievement Unlocked <Sparkles size={16} />
        </div>
        <div className="relative w-56 h-56 sm:w-64 sm:h-64 rounded-3xl p-[4px] overflow-hidden mb-5" style={{ boxShadow: `0 0 50px ${GOLD}AA, 0 0 110px #FF007F55` }}>
          <div className="absolute -inset-1/2" style={{ background: `conic-gradient(from 0deg, ${GOLD}, #FF007F, #00F0FF, ${GOLD})`, animation: "wrSpin 3s linear infinite" }} />
          <img src={`/achievements/${a.id}.webp`} alt={a.name} data-testid="achievement-unlock-popup-image"
               className="relative w-full h-full rounded-[20px] object-cover bg-[#0A051A]" />
          <span className="wr-tile-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/3" style={{ animation: "wrTileShine 2.2s ease-in-out 0.6s infinite" }} />
        </div>
        <h2 data-testid="achievement-unlock-popup-name" className="font-graffiti text-4xl sm:text-5xl leading-none mb-2"
            style={{ color: "#fff", textShadow: `0 0 20px ${GOLD}, 0 0 40px #FF007F88, 3px 3px 0 #000` }}>{a.name}</h2>
        <div className="text-sm font-mono text-[#E5DCFF] mb-1">{a.condition}</div>
        <div className="text-xs font-mono italic text-[#C9BDF0] mb-5">“{a.flavor}”</div>
        <button data-testid="achievement-unlock-popup-close" onClick={onClose}
                className="ach-pop-btn px-8 py-3 rounded-full font-heading uppercase tracking-[0.3em] text-sm text-[#0A051A] flex items-center gap-2 transition-transform hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(90deg, ${GOLD}, #FFE600)`, boxShadow: `0 0 24px ${GOLD}` }}>
          <Trophy size={16} strokeWidth={2.8} /> Awesome!
        </button>
      </div>
    </div>
  );
}

export default function AchievementUnlockPopup() {
  const { user } = useAuth();
  const loc = useLocation();
  const [queue, setQueue] = useState([]);
  const ready = !!user?.account_name;

  const check = useCallback(() => {
    if (!ready) return;
    api.get("/achievements/unlock-popups").then((r) => {
      const fresh = r.data || [];
      if (!fresh.length) return;
      api.post("/achievements/unlock-popups/ack", { ids: fresh.map((a) => a.id) }).catch(() => {});
      setQueue((q) => [...q, ...fresh.filter((a) => !q.some((x) => x.id === a.id))]);
    }).catch(() => {});
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    const stop = visibleInterval(check, 20000);
    window.addEventListener("achievements-check", check);
    return () => { stop(); window.removeEventListener("achievements-check", check); };
  }, [ready, check]);

  useEffect(() => { const t = setTimeout(check, 1200); return () => clearTimeout(t); }, [loc.pathname, check]);

  const close = useCallback(() => setQueue((q) => q.slice(1)), []);
  if (!queue.length) return null;
  return <PopupCard key={queue[0].id} a={queue[0]} onClose={close} />;
}
